/* eslint-disable @typescript-eslint/no-empty-function */
import { CompanyPortalProperties, DEFAULT_COMPANY_PORTAL_PROPERTIES, Tablet } from "src/models/company"
import { Community, Company, UserProfile } from "@models/user"
import React, { createContext, useContext, useState } from "react"

interface IUserContext {
  user: UserProfile | null
  setUser: (user: UserProfile | null) => void
  userCommunities: Community[] | null
  setUserCommunities: (communities: Community[] | null) => void
  companyPortalProperties: CompanyPortalProperties
  setCompanyPortalProperties: (companyPortalProperties: CompanyPortalProperties) => void
  tablets: Tablet[]
  setTablets: (tablets: Tablet[]) => void
}
export const UserContext = createContext<IUserContext>({
  user: null, setUser: () => { },
  userCommunities: [], setUserCommunities: () => { },
  companyPortalProperties: DEFAULT_COMPANY_PORTAL_PROPERTIES, setCompanyPortalProperties: () => { },
  tablets: [], setTablets: () => { }
})

/** Returns the current user and company. Only use inside the app shell that renders after user is loaded (e.g. under BiPortal when initialized). Throws if user or user.company is not yet set. */
export function useUser(): UserProfile {
  const { user } = useContext(UserContext)
  if (!user?.company) {
    throw new Error("User or user.company is not loaded. Use useUser() only where the app has already initialized (e.g. inside main routes).")
  }
  return user
}

/** Returns the current user's company. Only use where user is guaranteed to be loaded. */
export function useCompany(): { company: Company, isOrgAdmin: boolean, companyId: string } {
  const company = useUser().company
  if (!company) {
    throw new Error("Company is not loaded. Use useCompany() only where the app has already initialized (e.g. inside main routes).")
  }
  return {
    company,
    isOrgAdmin: company.profile === "ORGANIZATION_ADMIN",
    companyId: company.companyId
  }
}

export function useCommunities(): { communities: Community[], isGatedCommunityAdminInAny: boolean, managerCommunities: Community[], setUserCommunities: (communities: Community[] | null) => void } {
  const { userCommunities, setUserCommunities } = useContext(UserContext)
  if (!userCommunities) {
    throw new Error("User communities are not loaded. Use useCommunities() only where the app has already initialized (e.g. inside main routes).")
  }
  const managerCommunities = userCommunities.filter((c) => c.profile === "GATED_COMMUNITY_ADMIN")
  const isGatedCommunityAdminInAny = managerCommunities.length > 0
  return {
    communities: userCommunities,
    isGatedCommunityAdminInAny,
    managerCommunities,
    setUserCommunities: setUserCommunities
  }
}

interface Props {
  children: React.ReactNode
}
export const UserProvider = ({ children }: Props) => {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [userCommunities, setUserCommunities] = useState<Community[] | null>(null)
  const [companyPortalProperties, setCompanyPortalProperties] = useState<CompanyPortalProperties>(DEFAULT_COMPANY_PORTAL_PROPERTIES)
  const [tablets, setTablets] = useState<Tablet[]>([])

  return (
    <UserContext.Provider value={{
      user, setUser,
      userCommunities, setUserCommunities,
      companyPortalProperties, setCompanyPortalProperties,
      tablets, setTablets
    }} >
      {children}
    </UserContext.Provider>
  )
}